/**
 * NetworkSelector Hook - Orchestrates mediator + store
 * Fetches network tenants on mount for admin users
 */

import { useEffect } from 'react';
import { logger, mediator } from '@/application/core';
import { GetNetworkTenantsQuery, type NetworkTenantsResponse } from '@/application/queries/networkTenants';
import {
  useNetworkTenants,
  useHostDomain,
  useNetworkSelectorLoading,
  useNetworkSelectorError,
  useSetNetworkSelectorData,
  useSetNetworkSelectorLoading,
  useSetNetworkSelectorError,
} from './network-selector.store';

export function useNetworkSelector() {
  const tenants = useNetworkTenants();
  const hostDomain = useHostDomain();
  const loading = useNetworkSelectorLoading();
  const error = useNetworkSelectorError();
  const setData = useSetNetworkSelectorData();
  const setLoading = useSetNetworkSelectorLoading();
  const setError = useSetNetworkSelectorError();

  useEffect(() => {
    let mounted = true;

    async function fetchTenants() {
      // Skip if already loaded
      if (tenants.length > 0) return;

      setError(null);
      setLoading(true);
      try {
        const result = await mediator.send<NetworkTenantsResponse>(new GetNetworkTenantsQuery());
        if (mounted) {
          setData(result.networkTenants, result.hostDomain);
        }
      } catch (err) {
        logger.error('NetworkSelector', 'Error fetching tenants:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch network tenants'));
        }
      }
    }

    fetchTenants();
    return () => { mounted = false; };
  }, []); // Run once on mount

  return { tenants, hostDomain, loading, error };
}
